"use client";

import { IconMinus, IconPlus, IconTrash } from "@tabler/icons-react";
import { format } from "date-fns";
import { cs, enUS, pl } from "date-fns/locale";
import { useLocale, useTranslations } from "next-intl";
import { toDateKey } from "@/features/addons/hooks/use-diet-addon-modal";

const dateFnsLocales = { pl, en: enUS, cz: cs };

const getDateFnsLocale = (locale: string) =>
  dateFnsLocales[locale as keyof typeof dateFnsLocales] ?? pl;

type DietAddonDayListProps = {
  selectedDates: Date[];
  selection: Record<string, number>;
  canAddMore: boolean;
  onIncrement: (date: Date) => void;
  onDecrement: (date: Date) => void;
  onRemove: (date: Date) => void;
};

export const DietAddonDayList = ({
  selectedDates,
  selection,
  canAddMore,
  onIncrement,
  onDecrement,
  onRemove,
}: DietAddonDayListProps) => {
  const t = useTranslations();
  const locale = useLocale();
  const dateFnsLocale = getDateFnsLocale(locale);

  if (selectedDates.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-2.5 self-stretch py-10 text-center">
        <div className="flex size-14 items-center justify-center rounded-full bg-brand-white text-2xl text-brand-green2-300">
          +
        </div>
        <div className="flex flex-col gap-1">
          <p className="font-medium text-base text-brand-green2-300 leading-6.5 md:text-lg">
            {t("cart.configuration.addons.modal.emptyTitle")}
          </p>
          <p className="text-brand-grey5 text-sm leading-5.5">
            {t("cart.configuration.addons.modal.emptyDescription")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <ul className="flex flex-1 flex-col gap-1.5 self-stretch">
      {selectedDates.map((date) => {
        const quantity = selection[toDateKey(date)] ?? 0;
        const label = format(date, "EEE, d MMMM", { locale: dateFnsLocale });

        return (
          <li
            className="flex items-center justify-between gap-2 rounded-lg bg-brand-white px-4 py-2"
            key={toDateKey(date)}
          >
            <span className="text-base text-brand-green1-300 capitalize leading-6">
              {label}
            </span>

            <div className="flex items-center gap-2 rounded-xl border border-brand-grey2-300 bg-brand-white p-[5px]">
              <button
                aria-label={
                  quantity > 1
                    ? t("cart.configuration.addons.modal.decrease")
                    : t("cart.configuration.addons.modal.removeDay", {
                        day: label,
                      })
                }
                className="flex size-10 items-center justify-center rounded-lg bg-brand-grey1-300 text-brand-green1-300 hover:bg-brand-grey2-300"
                onClick={() =>
                  quantity > 1 ? onDecrement(date) : onRemove(date)
                }
                type="button"
              >
                {quantity > 1 ? (
                  <IconMinus className="size-4" stroke={2} />
                ) : (
                  <IconTrash className="size-4" stroke={2} />
                )}
              </button>

              <span className="min-w-6 text-center text-base text-brand-green1-300 leading-6">
                {quantity}
              </span>

              <button
                aria-label={t("cart.configuration.addons.modal.increase")}
                className="flex size-10 items-center justify-center rounded-lg bg-brand-grey1-300 text-brand-green1-300 hover:bg-brand-grey2-300 disabled:cursor-not-allowed disabled:opacity-40"
                disabled={!canAddMore}
                onClick={() => onIncrement(date)}
                type="button"
              >
                <IconPlus className="size-4" stroke={2} />
              </button>
            </div>
          </li>
        );
      })}
    </ul>
  );
};
